import { MenuItem, TextField, Typography } from '@mui/material';

import {
  NAME_STYLES,
  SIGNATURE_FONTS,
  type NameStyle,
  type SignatureFont,
} from './typedSignature';

interface TypedSignatureFieldsProps {
  /** The director's name from their record — the placeholder and the fallback. */
  fullName: string;
  typedName: string;
  onTypedNameChange: (value: string) => void;
  nameStyle: NameStyle;
  onNameStyleChange: (value: NameStyle) => void;
  font: SignatureFont;
  onFontChange: (value: SignatureFont) => void;
  /** Already has the name style applied — what will actually be rasterised. */
  previewText: string;
}

/**
 * The "type your name" half of the add-signature dialog.
 *
 * Split out of `AddSignatureDialog` because the two modes have nothing in
 * common beyond producing an image, and keeping both inline pushed that
 * component past the function-length limit.
 */
export const TypedSignatureFields = ({
  fullName,
  typedName,
  onTypedNameChange,
  nameStyle,
  onNameStyleChange,
  font,
  onFontChange,
  previewText,
}: TypedSignatureFieldsProps) => (
  <div className="flex flex-col gap-4">
    <TextField
      label="Name to sign with"
      value={typedName}
      onChange={(event) => {
        onTypedNameChange(event.target.value);
      }}
      placeholder={fullName}
      helperText={
        fullName === ''
          ? 'Type the name you sign with.'
          : 'Leave blank to use the name on your record.'
      }
      autoComplete="name"
      fullWidth
      size="small"
    />

    <div className="flex flex-col gap-4 sm:flex-row">
      <TextField
        select
        label="Style"
        value={nameStyle}
        size="small"

        onChange={(event) => {
          onNameStyleChange(event.target.value as NameStyle);
        }}
        className="w-full sm:w-1/2"
      >
        {Object.entries(NAME_STYLES).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Font"
        value={font}
        onChange={(event) => {
          onFontChange(event.target.value as SignatureFont);
        }}
        className="w-full sm:w-1/2"
        size="small"
      >
        {Object.entries(SIGNATURE_FONTS).map(([value, { label }]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
    </div>

    <div className="flex flex-col gap-1.5">
      <Typography variant="caption" color="text.secondary" component="p">
        Preview
      </Typography>
      <div className="flex h-28 items-center justify-center overflow-hidden rounded-lg border border-dashed border-divider bg-surface px-4">
        {previewText === '' ? (
          <Typography variant="body2" color="text.secondary">
            Type a name to see it here.
          </Typography>
        ) : (
          <span
            // The preview renders the same text in the same face the canvas
            // rasterises, so what a director approves here is what gets saved.
            style={{ fontFamily: SIGNATURE_FONTS[font].stack }}
            className="truncate text-[44px] leading-normal text-content"
          >
            {previewText}
          </span>
        )}
      </div>
    </div>
  </div>
);
